import React, { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { User, ChevronDown, Menu, X, Home, Info, Circle, HelpCircle, Users, LogOut, Bell } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import './HowItWorksHeader.css'; // Keep original CSS filename

const HowItWorksHeader = () => { // Changed component name to original
  const { isAuthenticated, loginWithRedirect, logout, user, isLoading } = useAuth0();
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const headerRef = useRef(null);
  const resourcesRef = useRef(null);
  const profileRef = useRef(null);
  const location = useLocation();
  
  // Check if current path is active
  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  // Close mobile menu when window is resized wider than mobile breakpoint
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileMenuOpen]);

  // Handle clicks outside of menus to close them
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
      if (resourcesRef.current && !resourcesRef.current.contains(event.target)) {
        setIsResourcesOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Prevent body scrolling when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    // Close any open dropdowns when toggling mobile menu
    if (!isMobileMenuOpen) {
      setIsResourcesOpen(false);
      setIsProfileOpen(false);
    }
  };

  const toggleResources = (e) => {
    e.stopPropagation();
    setIsResourcesOpen(!isResourcesOpen);
    if (isProfileOpen) setIsProfileOpen(false);
  };

  const toggleProfile = (e) => {
    e.stopPropagation();
    setIsProfileOpen(!isProfileOpen);
    if (isResourcesOpen) setIsResourcesOpen(false);
  };

  const handleLogin = () => {
    setIsMobileMenuOpen(false);
    loginWithRedirect();
  };

  const handleLogout = () => {
    setIsMobileMenuOpen(false);
    setIsProfileOpen(false);
    logout({ returnTo: window.location.origin });
  };

  const handleJoinNow = () => {
    setIsMobileMenuOpen(false);
    loginWithRedirect({
      authorizationParams: {
        screen_hint: 'signup',
      }
    });
  };

  const handleMobileNavItemClick = () => {
    setIsMobileMenuOpen(false);
    setIsResourcesOpen(false);
    setIsProfileOpen(false);
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user || !user.name) return '';
    
    const names = user.name.split(' ');
    if (names.length === 1) return names[0].charAt(0);
    return names[0].charAt(0) + names[names.length - 1].charAt(0);
  };

  // Show skeleton loader while Auth0 is initializing
  if (isLoading) {
    return (
      <header className="circledin-header">
        <div className="circledin-header-container">
          <div className="circledin-header-logo">Circledin</div>
          <div className="circledin-header-nav">
            {/* Skeleton placeholders */}
            <div className="circledin-header-skeleton-item"></div>
            <div className="circledin-header-skeleton-item"></div>
            <div className="circledin-header-skeleton-item"></div>
          </div>
          <div className="circledin-header-auth-section">
            <div className="circledin-header-skeleton-button"></div>
          </div>
        </div>
      </header>
    );
  }

  return (
    <>
      <div 
        className={`circledin-header-mobile-overlay ${isMobileMenuOpen ? 'active' : ''}`}
        onClick={() => setIsMobileMenuOpen(false)}
      />
      
      <header ref={headerRef} className="circledin-header">
        <div className="circledin-header-container">
          <Link to="/" className="circledin-header-logo">Circledin</Link>
          
          {/* Desktop Navigation */}
          <nav className={`circledin-header-nav ${isMobileMenuOpen ? 'open' : ''}`}>
            {/* Main Navigation Items - These appear on both desktop and mobile */}
            <div className="circledin-header-nav-item">
              <Link 
                to="/" 
                className={`circledin-header-nav-link ${isActive('/') ? 'active' : ''}`}
                onClick={handleMobileNavItemClick}
              >
                <Home size={20} /> 
                <span className="circledin-header-nav-text">Home</span>
              </Link>
            </div>
            
            <div className="circledin-header-nav-item">
              <Link 
                to="/how-it-works" 
                className={`circledin-header-nav-link ${isActive('/how-it-works') ? 'active' : ''}`}
                onClick={handleMobileNavItemClick}
              >
                <Info size={20} /> 
                <span className="circledin-header-nav-text">How It Works</span>
              </Link>
            </div>
            
            <div className="circledin-header-nav-item">
              <Link 
                to="/all-circles" 
                className={`circledin-header-nav-link ${isActive('/all-circles') ? 'active' : ''}`}
                onClick={handleMobileNavItemClick}
              >
                <Circle size={20} /> 
                <span className="circledin-header-nav-text">All Circles</span>
              </Link>
            </div>
            
            <div className="circledin-header-nav-item circledin-header-nav-dropdown" ref={resourcesRef}>
              <button 
                className={`circledin-header-nav-dropdown-toggle ${isActive('/resources') || isActive('/help') ? 'active' : ''}`}
                onClick={toggleResources}
                aria-expanded={isResourcesOpen}
              >
                <HelpCircle size={20} />
                <span className="circledin-header-nav-text">Resources</span>
                <ChevronDown 
                  size={16} 
                  className={`circledin-header-chevron ${isResourcesOpen ? 'open' : ''}`} 
                />
              </button>
              <div className={`circledin-header-dropdown-menu ${isResourcesOpen ? 'show' : ''}`}>
                <a 
                  href="https://circledin.zendesk.com/hc/en-us" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="circledin-header-dropdown-item"
                  onClick={handleMobileNavItemClick}
                >
                  <HelpCircle size={18} />
                  <span>Help Center</span>
                </a>
                <Link 
                  to="/how-it-works#faq" 
                  className="circledin-header-dropdown-item"
                  onClick={handleMobileNavItemClick}
                >
                  <Info size={18} />
                  <span>FAQ</span>
                </Link>
              </div>
            </div>

            <div className="circledin-header-nav-item">
              <Link 
                to="/about-us" 
                className={`circledin-header-nav-link ${isActive('/about-us') ? 'active' : ''}`}
                onClick={handleMobileNavItemClick}
              >
                <Users size={20} />
                <span className="circledin-header-nav-text">About Us</span>
              </Link>
            </div>

            {/* Mobile-only account section - This should only appear on mobile view */}
            <div className="circledin-header-mobile-nav-section">
              {isAuthenticated ? (
                <>
                  <div className="circledin-header-mobile-nav-title">Your Account</div>
                  <div className="circledin-header-nav-item">
                    <Link 
                      to="/my-profile" 
                      className={`circledin-header-nav-link ${isActive('/my-profile') ? 'active' : ''}`}
                      onClick={handleMobileNavItemClick}
                    >
                      <User size={20} />
                      <span className="circledin-header-nav-text">My Profile</span>
                    </Link>
                  </div>
                  <div className="circledin-header-nav-item">
                    <Link 
                      to="/my-circles" 
                      className={`circledin-header-nav-link ${isActive('/my-circles') ? 'active' : ''}`}
                      onClick={handleMobileNavItemClick}
                    >
                      <Circle size={20} />
                      <span className="circledin-header-nav-text">My Circles</span>
                    </Link>
                  </div>
                  <div className="circledin-header-nav-item">
                    <Link 
                      to="/notifications" 
                      className={`circledin-header-nav-link ${isActive('/notifications') ? 'active' : ''}`}
                      onClick={handleMobileNavItemClick}
                    >
                      <Bell size={20} />
                      <span className="circledin-header-nav-text">Notifications</span>
                    </Link>
                  </div>
                  <div className="circledin-header-nav-item">
                    <button 
                      onClick={handleLogout} 
                      className="circledin-header-nav-link"
                    >
                      <LogOut size={20} />
                      <span className="circledin-header-nav-text">Logout</span>
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="circledin-header-nav-item">
                    <a 
                      href="#" 
                      onClick={(e) => {
                        e.preventDefault();
                        handleLogin();
                      }} 
                      className="circledin-header-nav-link"
                    >
                      <User size={20} />
                      <span className="circledin-header-nav-text">Login</span>
                    </a>
                  </div>
                  <div className="circledin-header-nav-item">
                    <button 
                      onClick={handleJoinNow}
                      className="circledin-header-nav-join-button"
                    >
                      <Users size={20} />
                      <span className="circledin-header-nav-text">Join Now</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </nav>
          
          {/* Authentication Section */}
          <div className="circledin-header-auth-section">
            {isAuthenticated ? (
              <div className="circledin-header-user-section" ref={profileRef}>
                <button 
                  className="circledin-header-user-button" 
                  onClick={toggleProfile}
                >
                  <div className="circledin-header-avatar">
                    {getUserInitials()}
                  </div>
                  <span>{user?.name?.split(' ')[0]}</span>
                  <ChevronDown 
                    size={16} 
                    className={`circledin-header-chevron ${isProfileOpen ? 'open' : ''}`} 
                  />
                </button>
                
                <div className={`circledin-header-dropdown-menu ${isProfileOpen ? 'show' : ''}`}>
                  <Link 
                    to="/my-profile" 
                    className="circledin-header-dropdown-item"
                    onClick={() => setIsProfileOpen(false)}
                  >
                    <User size={18} />
                    <span>My Profile</span>
                  </Link>
                  <Link 
                    to="/my-circles" 
                    className="circledin-header-dropdown-item"
                    onClick={() => setIsProfileOpen(false)}
                  >
                    <Circle size={18} />
                    <span>My Circles</span>
                  </Link>
                  <Link 
                    to="/notifications" 
                    className="circledin-header-dropdown-item"
                    onClick={() => setIsProfileOpen(false)}
                  >
                    <Bell size={18} />
                    <span>Notifications</span>
                  </Link>
                  <button 
                    onClick={handleLogout} 
                    className="circledin-header-dropdown-item circledin-header-dropdown-item-danger"
                  >
                    <LogOut size={18} />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            ) : (
              <>
                <a 
                  href="#" 
                  onClick={(e) => {
                    e.preventDefault();
                    handleLogin();
                  }} 
                  className="circledin-header-nav-link"
                  style={{ marginRight: '1rem' }}
                >
                  Login
                </a>
                <button className="circledin-header-join-button" onClick={handleJoinNow}>
                  Join Now
                </button>
              </>
            )}
            
            <button
              className={`circledin-header-mobile-toggle ${isMobileMenuOpen ? 'active' : ''}`}
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>
    </>
  );
};

export default HowItWorksHeader; // Original export name